import { Col, FormGroup, Infobox, Row } from '@zoom/components';
import { FormApi } from 'final-form';
import { TFunction } from 'i18next';
import React, { useState } from 'react';
import { Field } from 'react-final-form';
import { OnChange } from 'react-final-form-listeners';

import { finalFormInputHelper } from '../../../components/Inputs/RenderInput';
import { finalFormNumberInputHelper } from '../../../components/Inputs/RenderNumberInput';
import { SelectField } from '../../../components/Inputs/SelectField';
import { WhenFieldChanges } from '../../../components/WhenFieldChanges/WhenFieldChanges';
import { Queue, QueueChannelType } from '../../../graphql/components';
import { required } from '../../../utils/validations';
import { composeValidators } from '../../../utils/validations/composeValidators';
import { inRange } from '../../../utils/validations/inRange';
import { ADJUSTMENTS_CONSTRAINS } from '../config';
import { CONCURRENT_CHATS_INPUT, NAME_INPUT, QUEUE_DROPDOWN } from '../constants';
import { FormValues } from '../utils/values';

interface FormForecastBaseProps {
  form: FormApi<FormValues>;
  isEdit: boolean;
  isProduction: boolean;
  isView: boolean;
  queues: Queue[];
  t: TFunction;
}

export const isSelectedQueueChatType = (allQueues: Queue[], selectedQueueId: string) => {
  const selectedQueue = allQueues.find((queue) => queue.id === selectedQueueId);

  return selectedQueue?.channelType === QueueChannelType.Chat;
};

export const FormForecastBase: React.FC<FormForecastBaseProps> = ({
  t,
  queues,
  isEdit,
  isProduction,
  isView,
  form,
}) => {
  const { queueId: preSelectedQueueId } = form.getState().values;
  const [isChatQueue, setIsChatQueue] = useState(isSelectedQueueChatType(queues, preSelectedQueueId));

  return (
    <div className="form-column-l">
      <FormGroup>
        <WhenFieldChanges field="queueId" set="historyRange.startDateTime" to={undefined} />
        <WhenFieldChanges field="queueId" set="historyRange.endDateTime" to={undefined} />
        <WhenFieldChanges field="queueId" set="forecastRange.startDateTime" to={undefined} />
        <WhenFieldChanges field="queueId" set="forecastRange.endDateTime" to={undefined} />

        <OnChange name="queueId">
          {(queueId: string, previousQueueId: string) => {
            if (queueId !== previousQueueId) {
              if (isSelectedQueueChatType(queues, queueId)) {
                setIsChatQueue(true);
                form.change('concurrentChatsCount', ADJUSTMENTS_CONSTRAINS.concurrentChats.default);
              } else {
                setIsChatQueue(false);
                form.change('concurrentChatsCount', null);
              }
            }
          }}
        </OnChange>
        <Row>
          <Col>
            <Field
              name="name"
              validate={required}
              render={finalFormInputHelper(
                {
                  label: t('forecast.name'),
                  dataTestId: NAME_INPUT,
                  type: 'text',
                },
                isProduction || isView
              )}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <SelectField
              data-test-id={QUEUE_DROPDOWN}
              disabled={isEdit || isView}
              label="queue.queue"
              name="queueId"
              options={queues}
              t={t}
              validate={required}
              className="w-100"
            />
          </Col>
        </Row>
        {isChatQueue && (
          <>
            <Row>
              <Col>
                <Field
                  name="concurrentChatsCount"
                  validate={composeValidators(
                    required,
                    inRange(ADJUSTMENTS_CONSTRAINS.concurrentChats.min, ADJUSTMENTS_CONSTRAINS.concurrentChats.max)
                  )}
                  render={finalFormNumberInputHelper({
                    dataTestId: CONCURRENT_CHATS_INPUT,
                    disabled: isEdit || isView,
                    label: t('forecast.concurrentChatsCount'),
                    max: ADJUSTMENTS_CONSTRAINS.concurrentChats.max,
                    min: ADJUSTMENTS_CONSTRAINS.concurrentChats.min,
                    noValidation: false,
                    toCentre: false,
                    type: 'number',
                  })}
                />
              </Col>
            </Row>
            <Infobox>{t('forecast.concurrentChatsCountInfo')}</Infobox>
          </>
        )}
      </FormGroup>
    </div>
  );
};
